import FollowerCard from '@/components/FollowerCard';
import Loader from '@/components/Loader';
import ScrolledCard from '@/components/ScrolledCard';
import Constants from '@/Constants';
import useMouseActivity from '@/hooks/useMouseActivity';
import { FollowersBatchRequest } from '@/models/FollowersBatchRequest';
import { FollowersRequest } from '@/models/FollowersRequest';
import { PersonBatch, SocialPerson } from '@/models/Person';
import { ApplicationState } from '@/store';
import { batchFollowRequest, clearFollowers, clearFollowing, getFollowersRequest, getFollowingRequest } from '@/store/followers/action';
import React, { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Tab, Tabs } from 'react-bootstrap';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import './style.scss';

interface Props {
    show: boolean;
    setShow: CallableFunction;
    ownerId: number;
}

const FollowModal = ({ show, setShow, ownerId }: Props) => {
    const { strings, myPersonId, followers, following, totalFollowers, totalFollowing, immutableTotalFollowing, loading } = useSelector(
        (state: ApplicationState) => ({
            myPersonId: state.identity.data.person.id,
            strings: state.locale.data,
            followers: state.followers.data.followers,
            following: state.followers.data.following,
            totalFollowers: state.followers.data.totalFollowers,
            totalFollowing: state.followers.data.totalFollowing,
            immutableTotalFollowing: state.followers.data.immutableTotalFollowing,
            loading: state.followers.loading,
        }),
    );

    const dispatch = useDispatch();

    const dispatchBatchUpdate = () => {
        dispatch(
            batchFollowRequest({
                personId: myPersonId,
                listOfPerson: followActionsRef.current,
            } as FollowersBatchRequest),
        );
        setFollowActions([]);
        followActionsRef.current = [];
        setFollowersOffset(0);
        setFollowingOffset(0);
    };

    useMouseActivity({ onInactive: dispatchBatchUpdate });

    useEffect(() => {
        const beforeUnloadListener = (evt: BeforeUnloadEvent) => {
            evt.preventDefault();
            evt.returnValue = '';
            dispatchBatchUpdate();
        };

        window.addEventListener('beforeunload', beforeUnloadListener);

        return () => {
            window.removeEventListener('beforeunload', beforeUnloadListener);
        };
    }, []);

    const [followersOffset, setFollowersOffset] = useState<number>(0);
    const [followingOffset, setFollowingOffset] = useState<number>(0);
    const [followActions, setFollowActions] = useState<PersonBatch[]>([]);
    const selectedTab = localStorage.getItem(Constants.SELECTED_TAB_FOLLOW_MODAL) || 'followers';
    const followActionsRef = useRef(followActions);

    const followersCallback = useCallback(() => {
        if (followers.length < totalFollowers) {
            setFollowersOffset(followers.length);
        }
    }, [followers.length]);

    const followingCallback = useCallback(() => {
        if (following.length < immutableTotalFollowing) {
            setFollowingOffset(following.length);
        }
    }, [following.length]);

    useEffect(() => {
        getFollowers(followersOffset);
    }, [followersOffset]);

    useEffect(() => {
        getFollowing(followingOffset);
    }, [followingOffset]);

    const getFollowers = (offset = 0) => {
        dispatch(
            getFollowersRequest({
                personId: ownerId,
                pagination: { offset, limit: Constants.FOLLOWERS_LIMIT },
            } as FollowersRequest),
        );
    };

    const getFollowing = (offset = 0) => {
        dispatch(
            getFollowingRequest({
                personId: ownerId,
                pagination: { offset, limit: Constants.FOLLOWERS_LIMIT },
            } as FollowersRequest),
        );
    };

    const followersScrollRef: RefObject<HTMLDivElement> = useBottomScrollListener(followersCallback, { offset: 100 });
    const followingScrollRef: RefObject<HTMLDivElement> = useBottomScrollListener(followingCallback, { offset: 100 });

    useEffect(() => {
        if (show) {
            dispatch(clearFollowers());
            dispatch(clearFollowing());
            getFollowers();
            getFollowing();
        }
    }, [show]);

    const getFollowersTitle = () => `${totalFollowers} ${totalFollowers === 1 ? strings.FOLLOWER : strings.FOLLOWERS}`;

    const getFollowingTitle = () => `${totalFollowing} ${strings.FOLLOWING}`;

    const closeModal = () => {
        localStorage.removeItem(Constants.SELECTED_TAB_FOLLOW_MODAL);
        setShow(false);
        setFollowersOffset(0);
        setFollowingOffset(0);
        dispatchBatchUpdate();
    };

    const updateActions = (person: SocialPerson) => {
        setFollowActions((prevState) => {
            const itemIndex = prevState.findIndex((p) => p.personId === person.id);
            const result =
                itemIndex === -1
                    ? [...prevState, { personId: person.id, followedByMe: !person.followedByMe }]
                    : prevState.filter((p) => p.personId !== person.id);
            followActionsRef.current = result;
            return result;
        });
    };

    return (
        <Modal aria-labelledby="contained-modal-title-vcenter" centered className="follow-modal" onHide={closeModal} show={show}>
            <Modal.Body>
                <div className="follow-modal-container">
                    <i className="material-icons-outlined close-icon" onClick={closeModal}>
                        {Constants.icons.CLOSE}
                    </i>

                    <div className="follow-modal-container-body">
                        <Tabs defaultActiveKey={selectedTab} onSelect={dispatchBatchUpdate} unmountOnExit>
                            <Tab
                                disabled={totalFollowers === 0}
                                eventKey="followers"
                                tabClassName="follow-modal-container-body-tab"
                                title={getFollowersTitle()}
                            >
                                <ScrolledCard divRef={followersScrollRef} id="followers-container" list={followers}>
                                    {followers.map((follower: SocialPerson) => (
                                        <FollowerCard
                                            closeModal={closeModal}
                                            follower={follower}
                                            isLoggedUserProfile={ownerId === myPersonId}
                                            key={follower.id}
                                            showFollowButton={follower.id === myPersonId}
                                            updateActions={updateActions}
                                        />
                                    ))}
                                    {loading && <Loader />}
                                </ScrolledCard>
                            </Tab>
                            <Tab
                                disabled={totalFollowing === 0}
                                eventKey="following"
                                tabClassName="follow-modal-container-body-tab"
                                title={getFollowingTitle()}
                            >
                                <ScrolledCard divRef={followingScrollRef} id="following-container" list={following}>
                                    {following.map((follower: SocialPerson) => (
                                        <FollowerCard
                                            closeModal={closeModal}
                                            follower={follower}
                                            isLoggedUserProfile={ownerId === myPersonId}
                                            key={follower.id}
                                            showFollowButton={follower.id === myPersonId}
                                            updateActions={updateActions}
                                        />
                                    ))}
                                    {loading && <Loader />}
                                </ScrolledCard>
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default FollowModal;
